type RealtimeSkipStatus = {
  skip: boolean;
  reason?: string;
  disabledUntil?: number;
};

type RealtimeGuardState = {
  disabledUntil: number;
  backoffMs: number;
  failureCount: number;
  reason: string;
};

const STORAGE_DISABLED_UNTIL = 'naiz_realtime_disabled_until';
const STORAGE_FAILURE_COUNT = 'naiz_realtime_failure_count';
const STORAGE_LAST_FAILURE = 'naiz_realtime_last_failure';
const STORAGE_LAST_REASON = 'naiz_realtime_last_reason';

const BASE_BACKOFF_MS = 15000;
const MAX_BACKOFF_MS = 5 * 60 * 1000;
const FAILURE_WINDOW_MS = 5 * 60 * 1000;

let memoryDisabledUntil = 0;
let memoryFailureCount = 0;
let memoryLastFailure = 0;
let memoryLastReason = '';

const hasWindow = () => typeof window !== 'undefined';

const readNumber = (key: string): number => {
  if (!hasWindow()) return 0;
  try {
    const raw = window.localStorage.getItem(key);
    const value = raw ? Number(raw) : 0;
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
};

const readString = (key: string): string => {
  if (!hasWindow()) return '';
  try {
    return window.localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const writeValue = (key: string, value: string | number): void => {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Ignore storage write failures
  }
};

const loadFromStorage = () => {
  const disabledUntil = readNumber(STORAGE_DISABLED_UNTIL);
  const failureCount = readNumber(STORAGE_FAILURE_COUNT);
  const lastFailure = readNumber(STORAGE_LAST_FAILURE);
  const lastReason = readString(STORAGE_LAST_REASON);

  if (disabledUntil > memoryDisabledUntil) memoryDisabledUntil = disabledUntil;
  if (failureCount > 0) memoryFailureCount = failureCount;
  if (lastFailure > 0) memoryLastFailure = lastFailure;
  if (lastReason) memoryLastReason = lastReason;
};

export function shouldSkipRealtime(): RealtimeSkipStatus {
  if (hasWindow() && typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { skip: true, reason: 'offline' };
  }

  loadFromStorage();

  const now = Date.now();
  if (memoryDisabledUntil > now) {
    return {
      skip: true,
      reason: memoryLastReason || 'recent_failure',
      disabledUntil: memoryDisabledUntil,
    };
  }

  return { skip: false };
}

export function markRealtimeUnavailable(reason: string = 'unknown_error'): RealtimeGuardState {
  loadFromStorage();

  const now = Date.now();
  if (now - memoryLastFailure > FAILURE_WINDOW_MS) {
    memoryFailureCount = 0;
  }

  memoryFailureCount += 1;
  memoryLastFailure = now;
  memoryLastReason = reason;

  const backoffMs = Math.min(BASE_BACKOFF_MS * Math.pow(2, memoryFailureCount - 1), MAX_BACKOFF_MS);
  memoryDisabledUntil = now + backoffMs;

  writeValue(STORAGE_DISABLED_UNTIL, memoryDisabledUntil);
  writeValue(STORAGE_FAILURE_COUNT, memoryFailureCount);
  writeValue(STORAGE_LAST_FAILURE, memoryLastFailure);
  writeValue(STORAGE_LAST_REASON, memoryLastReason);

  return {
    disabledUntil: memoryDisabledUntil,
    backoffMs,
    failureCount: memoryFailureCount,
    reason: memoryLastReason,
  };
}

/**
 * Attempt to refresh the Supabase auth session before retrying a Realtime subscription.
 *
 * When a Realtime channel fails (CHANNEL_ERROR), the root cause is often an expired or
 * stale JWT. Simply retrying the subscription with the same token will fail again.
 * This helper calls supabase.auth.refreshSession() so the next subscription attempt
 * uses a fresh token.
 *
 * Returns true if the refresh succeeded (or no session exists), false on failure.
 * Network errors are caught and logged — the caller should still proceed with backoff.
 */
export async function refreshSessionBeforeRetry(source: string): Promise<boolean> {
  try {
    // Dynamic import to avoid circular dependency (realtimeGuard is imported by hooks
    // that are imported by components that import the supabase client)
    const { createClient } = await import('@/lib/supabase/client');
    const supabase = createClient();

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      // No session — nothing to refresh (anonymous user or already signed out)
      return true;
    }

    const { error } = await supabase.auth.refreshSession();
    if (error) {
      // Log but don't throw — caller will still backoff and retry
      if (hasWindow() && typeof console !== 'undefined') {
        console.warn(`[${source}] Session refresh before Realtime retry failed:`, error.message);
      }
      return false;
    }

    if (hasWindow() && typeof console !== 'undefined') {
      console.debug(`[${source}] Session refreshed before Realtime retry`);
    }
    return true;
  } catch (err) {
    // Network error during refresh — expected when auth service is down
    if (hasWindow() && typeof console !== 'undefined') {
      console.warn(`[${source}] Session refresh network error:`, err);
    }
    return false;
  }
}

export function resetRealtimeGuard(): void {
  memoryDisabledUntil = 0;
  memoryFailureCount = 0;
  memoryLastFailure = 0;
  memoryLastReason = '';

  if (!hasWindow()) return;
  try {
    window.localStorage.removeItem(STORAGE_DISABLED_UNTIL);
    window.localStorage.removeItem(STORAGE_FAILURE_COUNT);
    window.localStorage.removeItem(STORAGE_LAST_FAILURE);
    window.localStorage.removeItem(STORAGE_LAST_REASON);
  } catch {
    // Ignore storage failures
  }
}
